import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Wallet, Plus, Trash2, Save, Edit2, Building2, Droplets, Zap,
  Calculator, Wrench, ShoppingCart, Users, Megaphone, MoreHorizontal,
  Calendar, BarChart3, TrendingDown, ChevronLeft, ChevronRight, PlusCircle,
  Repeat, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatTHB = (v) => `${(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} THB`;

const CATEGORY_ICONS = {
  'Loyer': Building2, 'Eau': Droplets, 'Electricite': Zap,
  'Comptable': Calculator, 'Achat materiel': Wrench, 'Achat produit': ShoppingCart,
  'Salaire': Users, 'Communication': Megaphone, 'Autre': MoreHorizontal,
};

const CATEGORY_COLORS = {
  'Loyer': '#E16833', 'Eau': '#0EA5E9', 'Electricite': '#D4A853',
  'Comptable': '#6E5E52', 'Achat materiel': '#7C3AED', 'Achat produit': '#2D6A4F',
  'Salaire': '#EF4444', 'Communication': '#00B14F', 'Autre': '#888',
};

const RECURRENCE_LABELS = { none: 'Ponctuelle', monthly: 'Mensuelle', yearly: 'Annuelle' };
const RECURRENCE_COLORS = { none: 'bg-[#F3EDE5] text-[#6E5E52]', monthly: 'bg-[#0EA5E9]/10 text-[#0EA5E9]', yearly: 'bg-[#7C3AED]/10 text-[#7C3AED]' };

export const ExpensesTab = ({ embedded = false }) => {
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));

  const [showAdd, setShowAdd] = useState(false);
  const [newExpense, setNewExpense] = useState({ category: 'Loyer', amount: '', note: '', recurrence: 'none' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = view === 'month' ? { month: selectedMonth } : { year: selectedYear };
      const [catRes, expRes, sumRes] = await Promise.allSettled([
        axios.get(`${API}/admin/expenses/categories`),
        axios.get(`${API}/admin/expenses`, { params }),
        axios.get(`${API}/admin/expenses/summary`, { params }),
      ]);
      if (catRes.status === 'fulfilled') setCategories(catRes.value.data);
      if (expRes.status === 'fulfilled') setExpenses(expRes.value.data);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [selectedMonth, selectedYear, view]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addExpense = async () => {
    if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
      toast.error('Montant requis'); return;
    }
    try {
      await axios.post(`${API}/admin/expenses`, {
        category: newExpense.category,
        amount: parseFloat(newExpense.amount),
        month: selectedMonth,
        note: newExpense.note,
        recurrence: newExpense.recurrence,
      });
      toast.success('Charge ajoutee');
      setNewExpense({ category: categories[0] || 'Loyer', amount: '', note: '', recurrence: 'none' });
      setShowAdd(false);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const updateExpense = async (id) => {
    try {
      await axios.patch(`${API}/admin/expenses/${id}`, editData);
      toast.success('Modifie');
      setEditId(null);
      setEditData({});
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${id}`);
      toast.success('Supprime');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API}/admin/expenses/categories`, { name: newCatName.trim() });
      toast.success(`Categorie "${newCatName}" ajoutee`);
      setNewCatName('');
      setShowAddCat(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const generateRecurring = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/expenses/generate-recurring`, { target_month: selectedMonth });
      const count = res.data.generated;
      if (count > 0) {
        toast.success(`${count} charge${count > 1 ? 's' : ''} recurrente${count > 1 ? 's' : ''} generee${count > 1 ? 's' : ''}`);
        fetchAll();
      } else {
        toast.info('Toutes les charges recurrentes sont deja presentes pour ce mois');
      }
    } catch { toast.error('Erreur'); } finally { setGenerating(false); }
  };

  const navigateMonth = (dir) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const navigateYear = (dir) => {
    setSelectedYear(String(parseInt(selectedYear) + dir));
  };

  const monthLabel = (m) => {
    const [y, mo] = m.split('-');
    return new Date(y, mo - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const getIcon = (cat) => CATEGORY_ICONS[cat] || MoreHorizontal;
  const getColor = (cat) => CATEGORY_COLORS[cat] || '#888';

  const hasRecurring = expenses.some(e => e.recurrence && e.recurrence !== 'none');

  return (
    <div data-testid="expenses-tab">
      {/* Period selector */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            <button onClick={() => setView('month')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${view === 'month' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="expenses-view-month">Mensuel</button>
            <button onClick={() => setView('year')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${view === 'year' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="expenses-view-year">Annuel</button>
          </div>

          {view === 'month' ? (
            <div className="flex items-center gap-2">
              <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[#F3EDE5]"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium text-[#2D241D] min-w-[140px] text-center capitalize">{monthLabel(selectedMonth)}</span>
              <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[#F3EDE5]"><ChevronRight className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => navigateYear(-1)} className="p-1.5 rounded-lg hover:bg-[#F3EDE5]"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium text-[#2D241D] min-w-[60px] text-center">{selectedYear}</span>
              <button onClick={() => navigateYear(1)} className="p-1.5 rounded-lg hover:bg-[#F3EDE5]"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}

          <div className="ml-auto flex gap-2">
            {view === 'month' && (
              <Button size="sm" onClick={generateRecurring} disabled={generating}
                className="bg-[#0EA5E9]/10 hover:bg-[#0EA5E9]/20 text-[#0EA5E9] rounded-xl h-9 text-xs border border-[#0EA5E9]/20" data-testid="generate-recurring-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${generating ? 'animate-spin' : ''}`} /> Generer recurrentes
              </Button>
            )}
            <Button size="sm" onClick={() => setShowAddCat(true)}
              className="bg-[#F3EDE5] hover:bg-[#E5DCD3] text-[#2D241D] rounded-xl h-9 text-xs" data-testid="add-category-btn">
              <PlusCircle className="w-3.5 h-3.5 mr-1" /> Categorie
            </Button>
            <Button size="sm" onClick={() => setShowAdd(true)}
              className="bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-xl h-9 text-xs font-bold" data-testid="add-expense-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une charge
            </Button>
          </div>
        </div>
      </div>

      {/* Add custom category */}
      {showAddCat && (
        <div className="bg-white rounded-2xl p-4 border border-[#D4A853]/30 mb-4" data-testid="add-category-form">
          <h4 className="text-sm font-bold text-[#2D241D] mb-2">Nouvelle categorie</h4>
          <div className="flex gap-2">
            <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Nom de la categorie"
              className="rounded-xl border-[#E5DCD3] h-9 text-sm flex-1" data-testid="new-cat-name" />
            <Button size="sm" onClick={addCategory} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl h-9 text-xs" data-testid="save-cat-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAddCat(false)} className="text-[#888] h-9 text-xs">Annuler</Button>
          </div>
        </div>
      )}

      {/* Add expense form */}
      {showAdd && (
        <div className="bg-white rounded-2xl p-4 border border-[#D4A853]/30 mb-4" data-testid="add-expense-form">
          <h4 className="text-sm font-bold text-[#2D241D] mb-3">Nouvelle charge - {monthLabel(selectedMonth)}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <select value={newExpense.category} onChange={e => setNewExpense({...newExpense, category: e.target.value})}
              className="h-9 px-3 rounded-xl border border-[#E5DCD3] text-sm bg-white" data-testid="expense-category-select">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input type="number" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})}
              placeholder="Montant (THB)" className="rounded-xl border-[#E5DCD3] h-9 text-sm" data-testid="expense-amount" />
            <select value={newExpense.recurrence} onChange={e => setNewExpense({...newExpense, recurrence: e.target.value})}
              className="h-9 px-3 rounded-xl border border-[#E5DCD3] text-sm bg-white" data-testid="expense-recurrence-select">
              <option value="none">Ponctuelle</option>
              <option value="monthly">Recurrente mensuelle</option>
              <option value="yearly">Recurrente annuelle</option>
            </select>
            <Input value={newExpense.note} onChange={e => setNewExpense({...newExpense, note: e.target.value})}
              placeholder="Note (optionnel)" className="rounded-xl border-[#E5DCD3] h-9 text-sm" data-testid="expense-note" />
            <div className="flex gap-2">
              <Button size="sm" onClick={addExpense} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl h-9 text-xs flex-1" data-testid="save-expense-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-[#888] h-9 text-xs">x</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-[#D4A853] border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Summary Dashboard */}
          {summary && (
            <div className="space-y-4 mb-6">
              <div className="bg-white rounded-2xl p-5 border border-[#E5DCD3]" data-testid="expenses-total">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#EF4444]/10 flex items-center justify-center">
                      <TrendingDown className="w-6 h-6 text-[#EF4444]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6E5E52]">Total charges {view === 'month' ? monthLabel(selectedMonth) : selectedYear}</p>
                      <p className="text-2xl font-bold text-[#EF4444]">{formatTHB(summary.total)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#6E5E52]">{summary.count} ligne{summary.count > 1 ? 's' : ''}</p>
                    {hasRecurring && (
                      <p className="text-[10px] text-[#0EA5E9] flex items-center justify-end gap-1 mt-0.5">
                        <Repeat className="w-3 h-3" /> dont recurrentes
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {Object.keys(summary.by_category).length > 0 && (
                <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="expenses-by-category">
                  <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#0A422D]" /> Repartition par categorie
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(summary.by_category)
                      .sort((a, b) => b[1] - a[1])
                      .map(([cat, amount]) => {
                        const Icon = getIcon(cat);
                        const color = getColor(cat);
                        const pct = summary.total > 0 ? Math.round((amount / summary.total) * 100) : 0;
                        return (
                          <div key={cat} className="rounded-xl p-3 border border-[#E5DCD3] bg-[#FAFAF8]">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="w-4 h-4" style={{ color }} />
                              <span className="text-xs font-medium text-[#6E5E52] truncate">{cat}</span>
                            </div>
                            <p className="text-lg font-bold text-[#2D241D]">{formatTHB(amount)}</p>
                            <div className="mt-1.5 h-1.5 bg-[#F3EDE5] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                            </div>
                            <p className="text-[10px] text-[#6E5E52] mt-0.5">{pct}% du total</p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {view === 'year' && Object.keys(summary.by_month).length > 0 && (
                <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="expenses-by-month">
                  <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#0A422D]" /> Evolution mensuelle
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(summary.by_month).map(([month, amount]) => {
                      const maxAmount = Math.max(...Object.values(summary.by_month));
                      const pct = maxAmount > 0 ? Math.round((amount / maxAmount) * 100) : 0;
                      return (
                        <div key={month} className="flex items-center gap-3">
                          <span className="text-xs text-[#6E5E52] w-20 capitalize">{monthLabel(month).split(' ')[0]}</span>
                          <div className="flex-1 h-6 bg-[#F3EDE5] rounded-lg overflow-hidden">
                            <div className="h-full rounded-lg bg-[#E16833] flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(pct, 5)}%` }}>
                              <span className="text-[10px] text-white font-bold">{formatTHB(amount)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expenses detail table */}
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden" data-testid="expenses-table">
            <div className="px-4 py-3 border-b border-[#E5DCD3]">
              <h3 className="font-semibold text-[#2D241D] flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[#0A422D]" /> Detail des charges
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F3EDE5] text-left text-xs text-[#6E5E52]">
                    <th className="px-4 py-2">Categorie</th>
                    <th className="px-4 py-2 text-right">Montant</th>
                    <th className="px-4 py-2">Recurrence</th>
                    <th className="px-4 py-2">Mois</th>
                    <th className="px-4 py-2">Note</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-[#6E5E52] text-sm">
                      Aucune charge pour cette periode. Cliquez "Ajouter une charge" pour commencer.
                    </td></tr>
                  ) : expenses.map(exp => {
                    const Icon = getIcon(exp.category);
                    const color = getColor(exp.category);
                    const isEditing = editId === exp.id;
                    const rec = exp.recurrence || 'none';
                    return (
                      <tr key={exp.id} className="border-b border-[#F3EDE5] hover:bg-[#FAFAF8]" data-testid={`expense-row-${exp.id}`}>
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <select value={editData.category ?? exp.category} onChange={e => setEditData({...editData, category: e.target.value})}
                              className="h-7 px-2 rounded border border-[#E5DCD3] text-xs bg-white">
                              {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : (
                            <span className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5" style={{ color }} />
                              <span className="font-medium text-[#2D241D]">{exp.category}</span>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <Input type="number" value={editData.amount ?? exp.amount}
                              onChange={e => setEditData({...editData, amount: parseFloat(e.target.value) || 0})}
                              className="h-7 text-xs w-24 ml-auto rounded border-[#E5DCD3]" />
                          ) : (
                            <span className="font-bold text-[#2D241D]">{formatTHB(exp.amount)}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <select value={editData.recurrence ?? rec} onChange={e => setEditData({...editData, recurrence: e.target.value})}
                              className="h-7 px-2 rounded border border-[#E5DCD3] text-xs bg-white" data-testid="edit-recurrence">
                              <option value="none">Ponctuelle</option>
                              <option value="monthly">Mensuelle</option>
                              <option value="yearly">Annuelle</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${RECURRENCE_COLORS[rec]}`}>
                              {rec !== 'none' && <Repeat className="w-2.5 h-2.5" />}
                              {RECURRENCE_LABELS[rec]}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[#6E5E52] capitalize">{monthLabel(exp.month)}</td>
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <Input value={editData.note ?? exp.note} onChange={e => setEditData({...editData, note: e.target.value})}
                              className="h-7 text-xs rounded border-[#E5DCD3]" />
                          ) : (
                            <span className="text-xs text-[#6E5E52]">{exp.note || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isEditing ? (
                              <>
                                <button onClick={() => updateExpense(exp.id)} className="p-1 rounded hover:bg-[#F3EDE5] text-[#2D6A4F]" data-testid={`save-edit-${exp.id}`}>
                                  <Save className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => { setEditId(null); setEditData({}); }} className="p-1 rounded hover:bg-[#F3EDE5] text-[#888] text-xs">x</button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => { setEditId(exp.id); setEditData({}); }}
                                  className="p-1 rounded hover:bg-[#F3EDE5] text-[#6E5E52]" data-testid={`edit-expense-${exp.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                                <button onClick={() => deleteExpense(exp.id)}
                                  className="p-1 rounded hover:bg-[#F3EDE5] text-[#EF4444]" data-testid={`delete-expense-${exp.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
